import { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { fetchCoordinates } from './coordinates'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'

/**
 * Hook wrapper for coordinate fetching.
 * It fetches coordinates if all request fields are filled.
 *
 * @param customerFormData address data that the corresponding coordinates are requested for
 * @param googleAgreed boolean whether or not the user agreed to use google maps, because google API is used for the request
 * @returns latitude and longitude and error flag
 */
export const useCoordinates = (googleAgreed: boolean): boolean => {
  const [geoCodeError, setGeoCodeError] = useState(false)

  const { control, setValue } = useInstallationForm()

  const locationInformation = useWatch({
    control,
    name: [
      'generalSettings.location.street',
      'generalSettings.location.houseNumber',
      'generalSettings.location.postalCode',
      'generalSettings.location.location',
      'generalSettings.location.location',
      'generalSettings.location.country',
    ],
  })

  const [street, houseNumber, postalCode, location, country] = useDebounce(locationInformation, 500)

  const clearCoordinates = (): void => {
    setValue('gpsLatitude', '')
    setValue('gpsLongitude', '')
  }

  useEffect(() => {
    if (!googleAgreed || !street.trim() || !houseNumber.trim() || !postalCode.trim() || !location.trim()) {
      clearCoordinates()
      setGeoCodeError(false)
      return
    }

    setGeoCodeError(false)

    clearCoordinates()
    fetchCoordinates(
      {
        city: location,
        countryCode: country,
        zip: postalCode,
        streetName: street,
        streetNumber: houseNumber,
      },
      googleAgreed
    )
      .then(response => {
        const gpsLatitude = response?.latitude?.toString() ?? ''
        const gpsLongitude = response?.longitude?.toString() ?? ''

        setValue('gpsLatitude', gpsLatitude)
        setValue('gpsLongitude', gpsLongitude)

        setGeoCodeError(false)
      })
      .catch(() => {
        clearCoordinates()
        setGeoCodeError(true)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleAgreed, street, houseNumber, postalCode, location, country])

  return geoCodeError
}
